import { BehaviorSubject, of as ObservableOf } from 'rxjs';
import { delay, tap } from 'rxjs/operators';
import { PaginationMeta } from '../entities/pagination';

export interface Highlighter
{
    id?: number;
    refocus?: boolean;
}

export class HighlightResource<T> {
    protected readonly currentHighlightSubject$ = new BehaviorSubject<{
        index: number;
        meta: PaginationMeta;
    }>(null);

    private readonly highlightIdSubject$ = new BehaviorSubject<number>(null);
    private readonly highlightRefocusSubject$ = new BehaviorSubject<boolean>(false);

    get highlightId() {
        return this.highlightIdSubject$.getValue();
    }

    set highlightId(id: number) {
        this.highlightIdSubject$.next(id);
        this.refocus();
    }

    refocus() {
        // allows ability to scrollTo after loading correct page for table
        this.highlightRefocusSubject$.next(true);
    }

    buildHighlightOptions(): Highlighter {
        return {
            id: this.highlightIdSubject$.getValue(),
            refocus: this.highlightRefocusSubject$.getValue(),
        };
    }

    scrollHighlightedIntoView(data: T[], key = 'id') {
        const highlightedIndex = data.findIndex((d) => {
            return key in data ? data[key] === this.highlightId : false;
        });
        const obs = ObservableOf(this.currentHighlightSubject$.getValue());

        if (!this.highlightRefocusSubject$.getValue() || highlightedIndex === -1) {
            return obs;
        }

        return obs.pipe(
            delay(500),
            tap((res, ) => {
                const allRows = document.querySelectorAll('mat-row');
                if (allRows[highlightedIndex]) {
                    allRows[highlightedIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else {
                    console.warn(`Row not found at index (${highlightedIndex}) on page (${res.meta.currentPage}).`);
                }

                // remove refocus attribute after scroll (even if not found)
                this.highlightRefocusSubject$.next(false);
            }),
        );
    }
}
